/**
 * The vendor's marketplace fees (docs/Decisions.md D68 items 1 to 3, D72):
 * every fee with its split, the totals, and saving a card so the monthly
 * invoice is charged automatically (otherwise Stripe emails it, due in 30
 * days). A salesperson's own payouts are set up here too.
 */
import { useEffect, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import FeeTable from './FeeTable';
import PayoutAccountCard from './PayoutAccountCard';
import { Spinner, ErrorAlert } from './ui';
import { formatUsd } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

export default function VendorBilling({ vendor }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    marketApi.getVendorBilling(vendor.vendorId).then(setData).catch(setError);
  }, [vendor.vendorId]);

  async function saveCard() {
    setBusy(true);
    try {
      const here = window.location.href;
      const res = await marketApi.startVendorBillingSetup(vendor.vendorId, here, here);
      window.location.assign(res.url);
    } catch (err) {
      toast.error(err.message || 'Stripe could not be reached');
      setBusy(false);
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Spinner label="Loading fees..." />;
  return (
    <section className={cn(ui.card, 'mb-6')} aria-labelledby="fees-heading">
      <h2 id="fees-heading" className="mt-0 text-title-m">
        Fees and billing
      </h2>
      <p className={cn(ui.hint, 'mt-0')}>
        You pay a lead fee on deals sourced through an organization&apos;s marketplace, invoiced
        monthly. Deals you sell yourself are not charged the salesperson balance.
      </p>
      <div className={cn(ui.row, 'mb-4')}>
        <span>
          Total so far: <strong>{formatUsd(data.totals.vendorPaysCents)}</strong>
        </span>
        {data.hasCard ? (
          <span className={ui.pillSuccess}>Card on file</span>
        ) : (
          <button
            type="button"
            className={cn(ui.btnSecondary, ui.btnSm)}
            disabled={busy}
            onClick={saveCard}
          >
            Save a card for automatic payment
          </button>
        )}
      </div>
      <FeeTable fees={data.fees} label="Your marketplace fees" />
      <PayoutAccountCard kind="user" title="Your payouts as a salesperson or recommender" />
    </section>
  );
}
